import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiZap,
  FiLock,
  FiPackage,
  FiGlobe,
  FiCheckCircle,
  FiSmartphone,
  FiShield,
  FiArrowLeft,
  FiRefreshCw,
  FiKey,
  FiMail,
  FiUser,
  FiMapPin,
  FiBriefcase,
} from "react-icons/fi";
import styles from "./welcome.module.css";
import {
  loginUser,
  sendRegisterOtp,
  verifyRegisterOtp,
  resendRegisterOtp,
  sendForgotPasswordOtp,
  verifyForgotPasswordOtp,
  resendForgotPasswordOtp,
  resetPassword,
} from "../../../Services/api";

// ─────────────────────────────────────────────────────────────────────────────
// Save non-sensitive user profile to localStorage for display (name, shop).
// The actual auth session lives in the HTTP-Only cookie — not here.
// ─────────────────────────────────────────────────────────────────────────────
const saveUserProfile = (user) => {
  localStorage.setItem(
    "magizh_user",
    JSON.stringify({
      id: user.id,
      full_name: user.full_name,
      email: user.email,
      phone: user.phone,
      shopName: user.shop_name,
      district: user.district,
      role: user.role,
    })
  );
};

const Welcome = () => {
  const navigate = useNavigate();

  // ── Login state ──────────────────────────────────────────────────────────
  const [loginData, setLoginData] = useState({ login: "", password: "" });
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  // ── Register state ───────────────────────────────────────────────────────
  const [showRegister, setShowRegister] = useState(false);
  const [registerStep, setRegisterStep] = useState("form"); // 'form' | 'otp'
  const [registerData, setRegisterData] = useState({
    full_name: "",
    email: "",
    phone: "",
    shop_name: "",
    district: "",
    password: "",
  });
  const [registerOtp, setRegisterOtp] = useState("");
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState("");
  const [registerFieldErrors, setRegisterFieldErrors] = useState({});
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [regTimer, setRegTimer] = useState(60);
  const [regResending, setRegResending] = useState(false);

  // ── Forgot Password state ────────────────────────────────────────────────
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState("phone"); // 'phone' | 'otp' | 'password' | 'success'
  const [forgotPhone, setForgotPhone] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [forgotResetToken, setForgotResetToken] = useState("");
  const [forgotNewPassword, setForgotNewPassword] = useState("");
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState("");
  const [showForgotPass, setShowForgotPass] = useState(false);
  const [showForgotConfirmPass, setShowForgotConfirmPass] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");
  const [forgotTimer, setForgotTimer] = useState(60);
  const [forgotResending, setForgotResending] = useState(false);

  // ── Timers for OTP resend ────────────────────────────────────────────────
  useEffect(() => {
    let interval = null;
    if (showRegister && registerStep === "otp" && regTimer > 0) {
      interval = setInterval(() => {
        setRegTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [showRegister, registerStep, regTimer]);

  useEffect(() => {
    let interval = null;
    if (showForgotPassword && forgotStep === "otp" && forgotTimer > 0) {
      interval = setInterval(() => {
        setForgotTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [showForgotPassword, forgotStep, forgotTimer]);

  // ── Login Handlers ───────────────────────────────────────────────────────
  const handleLoginChange = (e) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
    if (loginError) setLoginError("");
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError("");

    try {
      const res = await loginUser({
        login: loginData.login,
        password: loginData.password,
      });

      const loggedInUser = res.data.data;

      if (
        ["inactive", "banned"].includes(
          String(loggedInUser?.status || "active").toLowerCase()
        )
      ) {
        throw new Error(
          `This account is ${String(loggedInUser.status).toLowerCase()} and cannot log in.`
        );
      }

      if (res.data.token) {
        localStorage.setItem("magizh_token", res.data.token);
      }

      saveUserProfile(loggedInUser);
      navigate("/home");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Login failed. Please check your credentials.";
      setLoginError(msg);
    } finally {
      setLoginLoading(false);
    }
  };

  // ── Register Handlers ────────────────────────────────────────────────────
  const handleRegisterChange = (e) => {
    setRegisterData({ ...registerData, [e.target.name]: e.target.value });
    if (registerFieldErrors[e.target.name]) {
      setRegisterFieldErrors({ ...registerFieldErrors, [e.target.name]: "" });
    }
    if (registerError) setRegisterError("");
  };

  // Step 1: Submit Register Form & Request WhatsApp OTP
  const handleRegisterFormSubmit = async (e) => {
    e.preventDefault();
    setRegisterLoading(true);
    setRegisterError("");
    setRegisterFieldErrors({});

    try {
      await sendRegisterOtp(registerData);
      setRegisterStep("otp");
      setRegTimer(60);
      setRegisterOtp("");
    } catch (err) {
      if (err?.response?.status === 422) {
        const errors = err.response.data.errors || {};
        setRegisterFieldErrors(errors);
        const firstError = Object.values(errors)[0];
        if (firstError) {
          setRegisterError(Array.isArray(firstError) ? firstError[0] : String(firstError));
        } else {
          setRegisterError(err.response.data.message || "Please check the entered information.");
        }
      } else {
        setRegisterError(
          err?.response?.data?.message || "Failed to send WhatsApp verification OTP. Please try again."
        );
      }
    } finally {
      setRegisterLoading(false);
    }
  };

  // Step 2: Verify Register WhatsApp OTP
  const handleVerifyRegisterOtp = async (e) => {
    e.preventDefault();
    if (!registerOtp || registerOtp.trim().length < 4) {
      setRegisterError("Please enter the 6-digit OTP sent to your WhatsApp.");
      return;
    }

    setRegisterLoading(true);
    setRegisterError("");

    try {
      const res = await verifyRegisterOtp({
        phone: registerData.phone,
        otp: registerOtp.trim(),
      });

      if (res.data.token) {
        localStorage.setItem("magizh_token", res.data.token);
      }

      saveUserProfile(res.data.data);
      setRegisterSuccess(true);

      setTimeout(() => {
        setRegisterSuccess(false);
        setShowRegister(false);
        setRegisterStep("form");
        setRegisterData({
          full_name: "",
          email: "",
          phone: "",
          shop_name: "",
          district: "",
          password: "",
        });
        setRegisterOtp("");
        navigate("/home");
      }, 1500);
    } catch (err) {
      setRegisterError(
        err?.response?.data?.message || "Invalid or expired OTP. Please check and try again."
      );
    } finally {
      setRegisterLoading(false);
    }
  };

  // Resend Register OTP
  const handleResendRegisterOtp = async () => {
    if (regTimer > 0 || regResending) return;
    setRegResending(true);
    setRegisterError("");

    try {
      await resendRegisterOtp({ phone: registerData.phone });
      setRegTimer(60);
    } catch (err) {
      setRegisterError(
        err?.response?.data?.message || "Failed to resend OTP. Please try again."
      );
    } finally {
      setRegResending(false);
    }
  };

  // ── Forgot Password Handlers ─────────────────────────────────────────────
  // Step 1: Send Forgot Password OTP
  const handleForgotPhoneSubmit = async (e) => {
    e.preventDefault();
    if (!forgotPhone || forgotPhone.trim().length < 3) {
      setForgotError("Please enter a valid WhatsApp number or Email.");
      return;
    }

    setForgotLoading(true);
    setForgotError("");

    try {
      await sendForgotPasswordOtp({ phone: forgotPhone.trim() });
      setForgotStep("otp");
      setForgotTimer(60);
      setForgotOtp("");
    } catch (err) {
      setForgotError(
        err?.response?.data?.message || "No registered account found with this WhatsApp number / Email."
      );
    } finally {
      setForgotLoading(false);
    }
  };

  // Step 2: Verify Forgot Password OTP
  const handleVerifyForgotOtp = async (e) => {
    e.preventDefault();
    if (!forgotOtp || forgotOtp.trim().length < 4) {
      setForgotError("Please enter the 6-digit verification code.");
      return;
    }

    setForgotLoading(true);
    setForgotError("");

    try {
      const res = await verifyForgotPasswordOtp({
        phone: forgotPhone.trim(),
        otp: forgotOtp.trim(),
      });

      setForgotResetToken(res.data.reset_token);
      setForgotStep("password");
    } catch (err) {
      setForgotError(
        err?.response?.data?.message || "Invalid or expired OTP. Please try again."
      );
    } finally {
      setForgotLoading(false);
    }
  };

  // Resend Forgot Password OTP
  const handleResendForgotOtp = async () => {
    if (forgotTimer > 0 || forgotResending) return;
    setForgotResending(true);
    setForgotError("");

    try {
      await resendForgotPasswordOtp({ phone: forgotPhone.trim() });
      setForgotTimer(60);
    } catch (err) {
      setForgotError(
        err?.response?.data?.message || "Failed to resend OTP. Please try again."
      );
    } finally {
      setForgotResending(false);
    }
  };

  // Step 3: Set New Password
  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!forgotNewPassword || forgotNewPassword.length < 6) {
      setForgotError("Password must be at least 6 characters long.");
      return;
    }

    if (forgotNewPassword !== forgotConfirmPassword) {
      setForgotError("Passwords do not match. Please check and try again.");
      return;
    }

    setForgotLoading(true);
    setForgotError("");

    try {
      await resetPassword({
        reset_token: forgotResetToken,
        password: forgotNewPassword,
      });

      setForgotStep("success");
      setLoginData((prev) => ({ ...prev, login: forgotPhone }));

      setTimeout(() => {
        setShowForgotPassword(false);
        setForgotStep("phone");
        setForgotPhone("");
        setForgotOtp("");
        setForgotResetToken("");
        setForgotNewPassword("");
        setForgotConfirmPassword("");
      }, 2000);
    } catch (err) {
      setForgotError(
        err?.response?.data?.message || "Failed to reset password. Please request a new OTP."
      );
    } finally {
      setForgotLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className={styles.wrapper}>
      {/* ───── Background Video ───── */}
      <video
        className={styles.bgVideo}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        controls={false}
        controlsList="nodownload"
        onContextMenu={(e) => e.preventDefault()}
      >
        <source
          src={`${process.env.PUBLIC_URL}/Videos/cloud.webm`}
          type="video/webm"
        />
      </video>

      {/* ───── Overlay gradient ───── */}
      <div className={styles.overlay} />

      {/* ───── Main Login Card ───── */}
      <div className={styles.container}>
        {/* Left branding panel */}
        <div className={styles.brandPanel}>
          <div
            className={styles.brandLogo}
            onClick={() => navigate("/home")}
          >
            <img
              src={`${process.env.PUBLIC_URL}/Assets/logo.png`}
              alt="Magizh One Logo"
              className={styles.brandLogoImg}
            />
          </div>

          <h1 className={styles.brandTitle}>
            One Platform.<br />
            Everything You Need.
          </h1>

          <p className={styles.brandSubtitle}>
            Your complete digital marketplace for Online Service Centers — explore,
            purchase and manage services, software tools, websites and more, all in one
            place.
          </p>

          <div className={styles.featureList}>
            {[
              { icon: <FiZap />, text: "All Services, One Place" },
              { icon: <FiPackage />, text: "Tools to Grow Your Business" },
              { icon: <FiGlobe />, text: "Build Your Digital Presence" },
              { icon: <FiLock />, text: "Manage Subscriptions Easily" },
            ].map((feature, index) => (
              <div key={index} className={styles.featureItem}>
                <span className={styles.featureIcon}>{feature.icon}</span>
                <span>{feature.text}</span>
              </div>
            ))}
          </div>

          <div className={styles.brandGlow} />
        </div>

        {/* Right login form */}
        <div className={styles.formPanel}>
          <div className={styles.registerHint}>
            Don&apos;t have an account?{" "}
            <button
              className={styles.registerLink}
              onClick={() => {
                setShowRegister(true);
                setRegisterStep("form");
                setLoginError("");
                setRegisterError("");
              }}
            >
              Register
            </button>
          </div>

          <div className={styles.formHeader}>
            <h2 className={styles.formTitle}>Welcome Back</h2>
            <p className={styles.formSubtitle}>
              Enter your details to access your Magizh One workspace.
            </p>
          </div>

          <form className={styles.form} onSubmit={handleLoginSubmit}>
            {/* Email or WhatsApp */}
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Email or WhatsApp Number</label>
              <div className={styles.inputWrapper}>
                <span className={styles.inputIcon}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                </span>
                <input
                  type="text"
                  name="login"
                  placeholder="Email or WhatsApp number"
                  className={styles.input}
                  value={loginData.login}
                  onChange={handleLoginChange}
                  required
                  autoComplete="username"
                />
              </div>
            </div>

            {/* Password */}
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Password</label>
              <div className={styles.inputWrapper}>
                <span className={styles.inputIcon}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </span>
                <input
                  type={showLoginPassword ? "text" : "password"}
                  name="password"
                  placeholder="Your password"
                  className={styles.input}
                  value={loginData.password}
                  onChange={handleLoginChange}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className={styles.eyeBtn}
                  onClick={() => setShowLoginPassword(!showLoginPassword)}
                  tabIndex={-1}
                  aria-label="Toggle password visibility"
                >
                  {showLoginPassword ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                  )}
                </button>
              </div>
            </div>

            {/* Error message */}
            {loginError && (
              <div className={styles.errorMsg}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                <span>{loginError}</span>
              </div>
            )}

            {/* Remember me & Forgot Password */}
            <div className={styles.rememberRow}>
              <label className={styles.checkLabel}>
                <input type="checkbox" className={styles.checkbox} />
                <span className={styles.checkmark} />
                Remember me
              </label>
              <button
                type="button"
                className={styles.forgotLink}
                onClick={() => {
                  setShowForgotPassword(true);
                  setForgotStep("phone");
                  setForgotPhone(loginData.login || "");
                  setForgotError("");
                }}
              >
                Forgot password?
              </button>
            </div>

            <button type="submit" className={styles.submitBtn} disabled={loginLoading}>
              {loginLoading ? (
                <span className={styles.spinner} />
              ) : (
                <>
                  <span>Sign In</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* ───── Register Modal ───── */}
      {showRegister && (
        <div className={styles.modalBackdrop} onClick={() => setShowRegister(false)}>
          <div
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              className={styles.modalClose}
              onClick={() => setShowRegister(false)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>

            <div className={styles.modalHeader}>
              <div className={styles.modalIcon}>
                {registerStep === "otp" ? <FiSmartphone size={26} /> : <FiUser size={26} />}
              </div>
              <h2 className={styles.modalTitle}>
                {registerStep === "otp" ? "WhatsApp Verification" : "Create Account"}
              </h2>
              <p className={styles.modalSubtitle}>
                {registerStep === "otp"
                  ? `Enter the 6-digit OTP sent to WhatsApp number +91 ${registerData.phone}`
                  : "Join Magizh One and unlock the full power of enterprise management."}
              </p>
            </div>

            {registerSuccess ? (
              <div className={styles.successMsg}>
                <span className={styles.successIcon}><FiCheckCircle /></span>
                <p>Account verified & created successfully! Redirecting...</p>
              </div>
            ) : registerStep === "form" ? (
              /* Step 1: Registration Form */
              <form className={styles.modalForm} onSubmit={handleRegisterFormSubmit}>
                {/* General error */}
                {registerError && (
                  <div className={styles.errorMsg}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                    <span>{registerError}</span>
                  </div>
                )}

                {/* Full Name */}
                <div className={styles.modalField}>
                  <label className={styles.modalLabel}>Full Name</label>
                  <div className={`${styles.modalInputWrapper} ${registerFieldErrors.full_name ? styles.inputError : ""}`}>
                    <span className={styles.modalInputIcon}>
                      <FiUser size={16} />
                    </span>
                    <input
                      type="text"
                      name="full_name"
                      placeholder="Your full name"
                      className={styles.modalInput}
                      value={registerData.full_name}
                      onChange={handleRegisterChange}
                      required
                    />
                  </div>
                  {registerFieldErrors.full_name && (
                    <span className={styles.fieldError}>{registerFieldErrors.full_name[0]}</span>
                  )}
                </div>

                {/* Email */}
                <div className={styles.modalField}>
                  <label className={styles.modalLabel}>Email Address</label>
                  <div className={`${styles.modalInputWrapper} ${registerFieldErrors.email ? styles.inputError : ""}`}>
                    <span className={styles.modalInputIcon}>
                      <FiMail size={16} />
                    </span>
                    <input
                      type="email"
                      name="email"
                      placeholder="name@company.com"
                      className={styles.modalInput}
                      value={registerData.email}
                      onChange={handleRegisterChange}
                      required
                    />
                  </div>
                  {registerFieldErrors.email && (
                    <span className={styles.fieldError}>{registerFieldErrors.email[0]}</span>
                  )}
                </div>

                {/* WhatsApp Number */}
                <div className={styles.modalField}>
                  <label className={styles.modalLabel}>WhatsApp Number (for OTP)</label>
                  <div className={`${styles.modalInputWrapper} ${registerFieldErrors.phone ? styles.inputError : ""}`}>
                    <span className={styles.modalInputIcon}>
                      <FiSmartphone size={16} />
                    </span>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="10-digit WhatsApp number"
                      className={styles.modalInput}
                      value={registerData.phone}
                      onChange={handleRegisterChange}
                      maxLength={15}
                      required
                    />
                  </div>
                  {registerFieldErrors.phone && (
                    <span className={styles.fieldError}>{registerFieldErrors.phone[0]}</span>
                  )}
                </div>

                {/* Business Name */}
                <div className={styles.modalField}>
                  <label className={styles.modalLabel}>Business Name</label>
                  <div className={`${styles.modalInputWrapper} ${registerFieldErrors.shop_name ? styles.inputError : ""}`}>
                    <span className={styles.modalInputIcon}>
                      <FiBriefcase size={16} />
                    </span>
                    <input
                      type="text"
                      name="shop_name"
                      placeholder="Your business name"
                      className={styles.modalInput}
                      value={registerData.shop_name}
                      onChange={handleRegisterChange}
                      required
                    />
                  </div>
                  {registerFieldErrors.shop_name && (
                    <span className={styles.fieldError}>{registerFieldErrors.shop_name[0]}</span>
                  )}
                </div>

                {/* District */}
                <div className={styles.modalField}>
                  <label className={styles.modalLabel}>District</label>
                  <div className={`${styles.modalInputWrapper} ${registerFieldErrors.district ? styles.inputError : ""}`}>
                    <span className={styles.modalInputIcon}>
                      <FiMapPin size={16} />
                    </span>
                    <input
                      type="text"
                      name="district"
                      placeholder="Your district"
                      className={styles.modalInput}
                      value={registerData.district}
                      onChange={handleRegisterChange}
                      required
                    />
                  </div>
                  {registerFieldErrors.district && (
                    <span className={styles.fieldError}>{registerFieldErrors.district[0]}</span>
                  )}
                </div>

                {/* Password */}
                <div className={styles.modalField}>
                  <label className={styles.modalLabel}>Password</label>
                  <div className={`${styles.modalInputWrapper} ${registerFieldErrors.password ? styles.inputError : ""}`}>
                    <span className={styles.modalInputIcon}>
                      <FiLock size={16} />
                    </span>
                    <input
                      type={showRegPassword ? "text" : "password"}
                      name="password"
                      placeholder="Min. 6 characters"
                      className={styles.modalInput}
                      value={registerData.password}
                      onChange={handleRegisterChange}
                      required
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className={styles.eyeBtn}
                      onClick={() => setShowRegPassword(!showRegPassword)}
                      tabIndex={-1}
                      aria-label="Toggle password visibility"
                    >
                      {showRegPassword ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                      )}
                    </button>
                  </div>
                  {registerFieldErrors.password && (
                    <span className={styles.fieldError}>{registerFieldErrors.password[0]}</span>
                  )}
                </div>

                <button
                  type="submit"
                  className={styles.modalSubmitBtn}
                  disabled={registerLoading}
                >
                  {registerLoading ? (
                    <span className={styles.spinner} />
                  ) : (
                    <>
                      <span>Continue & Send OTP (WhatsApp & Email)</span>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </>
                  )}
                </button>

                <p className={styles.modalFooterText}>
                  Already have an account?{" "}
                  <button
                    type="button"
                    className={styles.switchToLogin}
                    onClick={() => setShowRegister(false)}
                  >
                    Sign In
                  </button>
                </p>
              </form>
            ) : (
              /* Step 2: WhatsApp OTP Verification */
              <form className={styles.modalForm} onSubmit={handleVerifyRegisterOtp}>
                <div className={styles.otpNotice}>
                  <span className={styles.otpNoticeIcon}>💬</span>
                  <div className={styles.otpNoticeText}>
                    A 6-digit verification code has been dispatched to WhatsApp <strong>+91 {registerData.phone}</strong> and Email <strong>{registerData.email}</strong>.
                  </div>
                </div>

                {registerError && (
                  <div className={styles.errorMsg}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                    <span>{registerError}</span>
                  </div>
                )}

                <div className={styles.otpBoxContainer}>
                  <label className={styles.modalLabel}>Enter 6-Digit OTP</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="••••••"
                    className={styles.otpBigInput}
                    value={registerOtp}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      setRegisterOtp(val);
                      if (registerError) setRegisterError("");
                    }}
                    autoFocus
                    required
                  />
                </div>

                <div className={styles.otpActionRow}>
                  <span className={styles.otpTimerText}>
                    {regTimer > 0 ? `Resend code in ${regTimer}s` : "Didn't receive OTP?"}
                  </span>
                  <button
                    type="button"
                    className={`${styles.resendBtn} ${regTimer > 0 ? styles.resendBtnDisabled : ""}`}
                    onClick={handleResendRegisterOtp}
                    disabled={regTimer > 0 || regResending}
                  >
                    {regResending ? (
                      <span className={styles.spinner} style={{ width: 14, height: 14 }} />
                    ) : (
                      <>
                        <FiRefreshCw size={13} />
                        <span>Resend OTP</span>
                      </>
                    )}
                  </button>
                </div>

                <button
                  type="submit"
                  className={styles.modalSubmitBtn}
                  disabled={registerLoading || registerOtp.length < 4}
                >
                  {registerLoading ? (
                    <span className={styles.spinner} />
                  ) : (
                    <>
                      <span>Verify & Complete Registration</span>
                      <FiCheckCircle size={18} />
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className={styles.backBtn}
                  onClick={() => {
                    setRegisterStep("form");
                    setRegisterError("");
                  }}
                >
                  <FiArrowLeft size={15} />
                  <span>Edit Registration Details</span>
                </button>
              </form>
            )}

            {/* Decorative blobs */}
            <div className={styles.modalBlob1} />
            <div className={styles.modalBlob2} />
          </div>
        </div>
      )}

      {/* ───── Forgot Password Modal ───── */}
      {showForgotPassword && (
        <div className={styles.modalBackdrop} onClick={() => setShowForgotPassword(false)}>
          <div
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              className={styles.modalClose}
              onClick={() => setShowForgotPassword(false)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>

            {/* Step indicators */}
            <div className={styles.stepTracker}>
              <div
                className={`${styles.stepPill} ${
                  forgotStep === "phone"
                    ? styles.stepPillActive
                    : ["otp", "password", "success"].includes(forgotStep)
                    ? styles.stepPillCompleted
                    : ""
                }`}
              >
                1. WhatsApp / Email
              </div>
              <div className={styles.stepDivider} />
              <div
                className={`${styles.stepPill} ${
                  forgotStep === "otp"
                    ? styles.stepPillActive
                    : ["password", "success"].includes(forgotStep)
                    ? styles.stepPillCompleted
                    : ""
                }`}
              >
                2. OTP
              </div>
              <div className={styles.stepDivider} />
              <div
                className={`${styles.stepPill} ${
                  forgotStep === "password"
                    ? styles.stepPillActive
                    : forgotStep === "success"
                    ? styles.stepPillCompleted
                    : ""
                }`}
              >
                3. New Password
              </div>
            </div>

            <div className={styles.modalHeader}>
              <div className={styles.modalIcon}>
                {forgotStep === "phone" && <FiSmartphone size={26} />}
                {forgotStep === "otp" && <FiShield size={26} />}
                {forgotStep === "password" && <FiKey size={26} />}
                {forgotStep === "success" && <FiCheckCircle size={26} />}
              </div>
              <h2 className={styles.modalTitle}>
                {forgotStep === "phone" && "Reset Password"}
                {forgotStep === "otp" && "Verify OTP (WhatsApp & Email)"}
                {forgotStep === "password" && "Create New Password"}
                {forgotStep === "success" && "Password Reset!"}
              </h2>
              <p className={styles.modalSubtitle}>
                {forgotStep === "phone" &&
                  "Enter your registered WhatsApp number or Email to receive a secure recovery code."}
                {forgotStep === "otp" &&
                  `Enter the 6-digit OTP sent to your WhatsApp number and registered Email`}
                {forgotStep === "password" &&
                  "Enter and confirm your new account password (min. 6 characters)."}
                {forgotStep === "success" &&
                  "Your password has been reset successfully! Redirecting to login..."}
              </p>
            </div>

            {/* Error notice */}
            {forgotError && (
              <div className={styles.errorMsg} style={{ marginBottom: 14 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                <span>{forgotError}</span>
              </div>
            )}

            {/* Step 1: Input WhatsApp Number */}
            {forgotStep === "phone" && (
              <form className={styles.modalForm} onSubmit={handleForgotPhoneSubmit}>
                <div className={styles.modalField}>
                  <label className={styles.modalLabel}>Registered WhatsApp Number or Email</label>
                  <div className={styles.modalInputWrapper}>
                    <span className={styles.modalInputIcon}>
                      <FiSmartphone size={16} />
                    </span>
                    <input
                      type="text"
                      placeholder="10-digit WhatsApp number or Email"
                      className={styles.modalInput}
                      value={forgotPhone}
                      onChange={(e) => {
                        setForgotPhone(e.target.value);
                        if (forgotError) setForgotError("");
                      }}
                      maxLength={100}
                      required
                      autoFocus
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className={styles.modalSubmitBtn}
                  disabled={forgotLoading || !forgotPhone.trim()}
                >
                  {forgotLoading ? (
                    <span className={styles.spinner} />
                  ) : (
                    <>
                      <span>Send Recovery OTP</span>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className={styles.backBtn}
                  onClick={() => setShowForgotPassword(false)}
                >
                  <FiArrowLeft size={15} />
                  <span>Back to Sign In</span>
                </button>
              </form>
            )}

            {/* Step 2: OTP Verification */}
            {forgotStep === "otp" && (
              <form className={styles.modalForm} onSubmit={handleVerifyForgotOtp}>
                <div className={styles.otpNotice}>
                  <span className={styles.otpNoticeIcon}>💬</span>
                  <div className={styles.otpNoticeText}>
                    A 6-digit password reset OTP has been dispatched to your WhatsApp number and registered Email.
                  </div>
                </div>

                <div className={styles.otpBoxContainer}>
                  <label className={styles.modalLabel}>Enter 6-Digit OTP</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="••••••"
                    className={styles.otpBigInput}
                    value={forgotOtp}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      setForgotOtp(val);
                      if (forgotError) setForgotError("");
                    }}
                    autoFocus
                    required
                  />
                </div>

                <div className={styles.otpActionRow}>
                  <span className={styles.otpTimerText}>
                    {forgotTimer > 0 ? `Resend code in ${forgotTimer}s` : "Didn't receive code?"}
                  </span>
                  <button
                    type="button"
                    className={`${styles.resendBtn} ${forgotTimer > 0 ? styles.resendBtnDisabled : ""}`}
                    onClick={handleResendForgotOtp}
                    disabled={forgotTimer > 0 || forgotResending}
                  >
                    {forgotResending ? (
                      <span className={styles.spinner} style={{ width: 14, height: 14 }} />
                    ) : (
                      <>
                        <FiRefreshCw size={13} />
                        <span>Resend OTP</span>
                      </>
                    )}
                  </button>
                </div>

                <button
                  type="submit"
                  className={styles.modalSubmitBtn}
                  disabled={forgotLoading || forgotOtp.length < 4}
                >
                  {forgotLoading ? (
                    <span className={styles.spinner} />
                  ) : (
                    <>
                      <span>Verify OTP</span>
                      <FiCheckCircle size={18} />
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className={styles.backBtn}
                  onClick={() => {
                    setForgotStep("phone");
                    setForgotError("");
                  }}
                >
                  <FiArrowLeft size={15} />
                  <span>Change WhatsApp Number</span>
                </button>
              </form>
            )}

            {/* Step 3: Enter New Password */}
            {forgotStep === "password" && (
              <form className={styles.modalForm} onSubmit={handleResetPasswordSubmit}>
                {/* New Password */}
                <div className={styles.modalField}>
                  <label className={styles.modalLabel}>New Password</label>
                  <div className={styles.modalInputWrapper}>
                    <span className={styles.modalInputIcon}>
                      <FiLock size={16} />
                    </span>
                    <input
                      type={showForgotPass ? "text" : "password"}
                      placeholder="Min. 6 characters"
                      className={styles.modalInput}
                      value={forgotNewPassword}
                      onChange={(e) => {
                        setForgotNewPassword(e.target.value);
                        if (forgotError) setForgotError("");
                      }}
                      required
                      autoFocus
                    />
                    <button
                      type="button"
                      className={styles.eyeBtn}
                      onClick={() => setShowForgotPass(!showForgotPass)}
                      tabIndex={-1}
                      aria-label="Toggle password visibility"
                    >
                      {showForgotPass ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className={styles.modalField}>
                  <label className={styles.modalLabel}>Confirm New Password</label>
                  <div className={styles.modalInputWrapper}>
                    <span className={styles.modalInputIcon}>
                      <FiLock size={16} />
                    </span>
                    <input
                      type={showForgotConfirmPass ? "text" : "password"}
                      placeholder="Re-enter new password"
                      className={styles.modalInput}
                      value={forgotConfirmPassword}
                      onChange={(e) => {
                        setForgotConfirmPassword(e.target.value);
                        if (forgotError) setForgotError("");
                      }}
                      required
                    />
                    <button
                      type="button"
                      className={styles.eyeBtn}
                      onClick={() => setShowForgotConfirmPass(!showForgotConfirmPass)}
                      tabIndex={-1}
                      aria-label="Toggle password visibility"
                    >
                      {showForgotConfirmPass ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className={styles.modalSubmitBtn}
                  disabled={forgotLoading || !forgotNewPassword || !forgotConfirmPassword}
                >
                  {forgotLoading ? (
                    <span className={styles.spinner} />
                  ) : (
                    <>
                      <span>Save New Password</span>
                      <FiCheckCircle size={18} />
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Step 4: Success */}
            {forgotStep === "success" && (
              <div className={styles.successMsg}>
                <span className={styles.successIcon}><FiCheckCircle /></span>
                <p>Password updated successfully! Redirecting to login...</p>
              </div>
            )}

            {/* Decorative blobs */}
            <div className={styles.modalBlob1} />
            <div className={styles.modalBlob2} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Welcome;